import type { AutoSpinStore } from "../autospin/AutoSpinStore";
import type { BalanceStore } from "../balance/BalanceStore";
import type { LocalizationService } from "../localization/LocalizationService";
import { failureModal, rejectionFailure, transportFailure, type SpinFailureKind } from "../system-modal/failureCopy";
import type { SystemModalConfig, SystemModalOutcome } from "../system-modal/SystemModalTypes";
import type { Log } from "@slot/presentation";

export interface SpinFailurePresenterDeps {
  autoSpinStore: AutoSpinStore;
  balanceStore: BalanceStore;
  localization: LocalizationService;
  showSystemModal: (config: SystemModalConfig) => Promise<SystemModalOutcome>;
  /** Every failure but a known rejection ends in a reload: the client's state is unverified. */
  reloadPage: () => void;
  /** Paints the boot grid again after a round that never presented. */
  restoreGrid: () => Promise<void>;
  log: Log;
}

/**
 * What the player sees when a round does not complete. A rejection the shell knows (insufficient
 * funds) is explained and dismissed; any other rejection, and every transport or engine failure,
 * offers only a reload (user, 2026-09-13). Both stop autospin BEFORE the coordinator idles (its
 * idle snapshot would fire the next autospin). The grid is put back only when the round never
 * showed a result; a settlement failure leaves the result on the reels.
 */
export class SpinFailurePresenter {
  public constructor(private readonly deps: SpinFailurePresenterDeps) {}

  public async onRejected(input: { statusCode?: number; statusMessage?: string; balanceAmount?: string }): Promise<void> {
    this.deps.log(`[SpinFailure] spin rejected code=${String(input.statusCode ?? "")} message="${input.statusMessage ?? ""}" balance=${input.balanceAmount ?? "n/a"}`);
    this.deps.autoSpinStore.stop();
    if (input.balanceAmount) this.deps.balanceStore.setBalanceMinor(input.balanceAmount);
    this.showFailure(rejectionFailure(input.statusMessage, input.statusCode));
    await this.restoreGrid("rejected spin");
  }

  public async onError(input: { errorMessage: string; kind: SpinFailureKind; statusMessage?: string; presented: boolean }): Promise<void> {
    this.deps.log(`[SpinFailure] round failed kind=${input.kind} code=${input.statusMessage ?? "none"} presented=${input.presented} message="${input.errorMessage}"`);
    this.deps.autoSpinStore.stop();
    this.showFailure(transportFailure(input.kind, input.statusMessage));
    if (!input.presented) await this.restoreGrid("round failure");
  }

  private showFailure(copy: ReturnType<typeof rejectionFailure>): void {
    const t = this.deps.localization;
    void this.deps.showSystemModal(failureModal(copy, (key, fallback) => t.t(key, fallback))).then((outcome) => {
      if (copy.reload && outcome === "primary") this.deps.reloadPage();
    });
  }

  private async restoreGrid(after: string): Promise<void> {
    try {
      await this.deps.restoreGrid();
    } catch (error) {
      this.deps.log(`[SpinFailure] grid restore failed after ${after}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
}
